import { Link } from "react-router-dom";
import AppLayout from "@/components/AppLayout";
import { ShieldCheck } from "lucide-react";

// Privacyverklaring — AVG-conforme uitleg over gegevensverwerking.
// Bedrijfsgegevens van de verwerkingsverantwoordelijke (ingevuld 2026-06).

const COMPANY = {
  name:    "Club-Fin.nl",
  brand:   "MeerjarenplanOnline.nl",
  contact: "Ed van Wijk",
  address: "Markt 13",
  city:    "3252 BC Goedereede",
  kvk:     "42158680",
  vat:     "NL004544424B16",
  email:   "info@club-fin.nl",
};

const SUB_PROCESSORS = [
  { name: "Mollie B.V.",           purpose: "Verwerking van betalingen en SEPA-mandaten",             region: "Nederland (EU)",       agreement: "https://www.mollie.com/nl/gdpr" },
  { name: "Resend, Inc.",          purpose: "Verzending van transactiemails (wachtwoord-reset, facturen, notificaties)", region: "VS + EU (SCC-compliant)", agreement: "https://resend.com/legal/dpa" },
  { name: "Emergent Labs",         purpose: "Applicatie-hosting (Kubernetes, MongoDB)",              region: "EU",                    agreement: "verzoek via privacy-contact" },
  { name: "Cloudflare, Inc.",      purpose: "DDoS-protectie en TLS-terminatie",                       region: "Wereldwijd (SCC + DPF)", agreement: "https://www.cloudflare.com/cloudflare-customer-dpa" },
];

const DATA_CATEGORIES = [
  { cat: "Accountgegevens",       fields: "E-mailadres, naam, bcrypt-hash van wachtwoord",   basis: "Uitvoering overeenkomst (art. 6 lid 1b AVG)",      retention: "Tot 2 jaar na opzegging, daarna pseudonimisatie" },
  { cat: "Factuurgegevens",        fields: "Verenigingsnaam, adres, postcode/plaats, KvK, BTW-nummer", basis: "Wettelijke verplichting (art. 6 lid 1c AVG) — Belastingdienst", retention: "7 jaar (fiscale bewaarplicht)" },
  { cat: "Financiële scenario's",  fields: "Historische cijfers, budgetten, investeringsplannen die je zelf invoert", basis: "Uitvoering overeenkomst", retention: "Tot je ze verwijdert of account opzegt" },
  { cat: "Betalingsinformatie",    fields: "Mollie customer-ID, mandaat-ID, subscription-ID, betaalmethode (iDEAL/creditcard)", basis: "Uitvoering overeenkomst", retention: "7 jaar (fiscaal), Mollie is verantwoordelijk voor kaart/IBAN zelf" },
  { cat: "Loggegevens & IP",       fields: "Login-tijdstippen, IP-adres bij inlog, brute-force-cooldown counters", basis: "Gerechtvaardigd belang (art. 6 lid 1f AVG) — beveiliging", retention: "Rolling 30 dagen" },
];

function Section({ title, children, testid }) {
  return (
    <section className="mb-8" data-testid={testid}>
      <h2 className="font-display font-black text-xl uppercase mb-3 tracking-tight">{title}</h2>
      <div className="text-sm leading-relaxed space-y-3 text-foreground/90">{children}</div>
    </section>
  );
}

export default function PrivacyPage() {
  const lastUpdated = "juni 2026";
  return (
    <AppLayout>
      <div className="max-w-3xl mx-auto" data-testid="privacy-page">
        <div className="mb-8">
          <div className="stat-label flex items-center gap-2">
            <ShieldCheck className="w-3.5 h-3.5" /> Privacy · AVG
          </div>
          <h1 className="font-display font-black text-4xl sm:text-5xl uppercase mt-1 leading-none">
            Privacyverklaring
          </h1>
          <p className="text-sm text-muted-foreground mt-3">
            Laatst bijgewerkt: {lastUpdated}. Deze verklaring beschrijft welke persoonsgegevens {COMPANY.name} ({COMPANY.brand}) verwerkt, waarom, hoe lang en met welke sub-verwerkers we werken.
          </p>
        </div>

        <Section title="1. Verwerkingsverantwoordelijke" testid="privacy-controller">
          <p>De verwerkingsverantwoordelijke voor de gegevens die je hier achterlaat is:</p>
          <div className="border-2 border-foreground p-4 bg-muted/30 font-mono-num text-sm">
            <div><b>{COMPANY.name}</b> <span className="text-muted-foreground">(handelsnaam {COMPANY.brand})</span></div>
            <div>Contactpersoon: {COMPANY.contact}</div>
            <div>{COMPANY.address}</div>
            <div>{COMPANY.city}</div>
            <div>KvK: {COMPANY.kvk}</div>
            <div>BTW: {COMPANY.vat}</div>
            <div>E-mail: <a className="underline" href={`mailto:${COMPANY.email}`}>{COMPANY.email}</a></div>
          </div>
        </Section>

        <Section title="2. Welke gegevens verwerken we?" testid="privacy-categories">
          <p>We verwerken alleen wat we écht nodig hebben voor de dienstverlening (dataminimalisatie, art. 5 AVG):</p>
          <div className="overflow-x-auto">
            <table className="w-full text-sm border-2 border-foreground">
              <thead>
                <tr className="bg-muted/40 border-b-2 border-foreground text-left">
                  <th className="p-2">Categorie</th>
                  <th className="p-2">Velden</th>
                  <th className="p-2">Grondslag</th>
                  <th className="p-2">Bewaartermijn</th>
                </tr>
              </thead>
              <tbody>
                {DATA_CATEGORIES.map((d) => (
                  <tr key={d.cat} className="border-b border-border align-top">
                    <td className="p-2 font-semibold">{d.cat}</td>
                    <td className="p-2 text-xs">{d.fields}</td>
                    <td className="p-2 text-xs">{d.basis}</td>
                    <td className="p-2 text-xs">{d.retention}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Section>

        <Section title="3. Doeleinden" testid="privacy-purposes">
          <ul className="list-disc pl-6 space-y-1">
            <li>Je in staat stellen te betalen voor en toegang te krijgen tot de applicatie.</li>
            <li>Facturen genereren en aanleveren (fiscaal verplicht).</li>
            <li>Herstelfunctie (wachtwoord vergeten, e-mailwijziging) en beveiliging (brute-force detectie).</li>
            <li>Verzenden van transactionele mails (betalingsbevestiging, herinneringen bij afloop proefperiode, factuur-PDF).</li>
          </ul>
          <p className="text-xs text-muted-foreground">
            We doen géén profilering, géén advertising-tracking, en verkopen géén gegevens aan derden.
          </p>
        </Section>

        <Section title="4. Bewaartermijnen" testid="privacy-retention">
          <p>
            <b>Facturen en betalingsgegevens</b> bewaren we 7 jaar (Algemene wet inzake rijksbelastingen art. 52 lid 4).
            <b> Accountgegevens</b> (naam, e-mail, adres) worden 2 jaar na beëindiging van je abonnement automatisch gepseudonimiseerd:
            we houden alleen een pseudoniem (bv. "Klant-2024-0042") + abonneenummer over voor auditspoor, en wissen naam, e-mail, adres, KvK en BTW.
          </p>
          <p>
            <b>Direct verwijderen</b> is óók mogelijk via de knop "Account verwijderen" op je account-pagina.
            Facturen blijven dan bewaard, met een bevroren snapshot van je gegevens op het moment van de betaling — dat is fiscaal verplicht.
          </p>
        </Section>

        <Section title="5. Sub-verwerkers" testid="privacy-subprocessors">
          <p>We schakelen deze partijen in voor specifieke taken. Met elke sub-verwerker is een verwerkersovereenkomst gesloten conform art. 28 AVG:</p>
          <div className="overflow-x-auto">
            <table className="w-full text-sm border-2 border-foreground">
              <thead>
                <tr className="bg-muted/40 border-b-2 border-foreground text-left">
                  <th className="p-2">Partij</th>
                  <th className="p-2">Doel</th>
                  <th className="p-2">Regio</th>
                  <th className="p-2">DPA</th>
                </tr>
              </thead>
              <tbody>
                {SUB_PROCESSORS.map((p) => (
                  <tr key={p.name} className="border-b border-border align-top">
                    <td className="p-2 font-semibold">{p.name}</td>
                    <td className="p-2 text-xs">{p.purpose}</td>
                    <td className="p-2 text-xs">{p.region}</td>
                    <td className="p-2 text-xs">
                      {p.agreement.startsWith("http")
                        ? <a href={p.agreement} target="_blank" rel="noopener noreferrer" className="underline">Bekijk</a>
                        : p.agreement}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Section>

        <Section title="6. Beveiligingsmaatregelen" testid="privacy-security">
          <ul className="list-disc pl-6 space-y-1">
            <li>Alle verkeer verloopt via HTTPS (TLS 1.3), afgedwongen door Cloudflare.</li>
            <li>Wachtwoorden worden als bcrypt-hash opgeslagen — niet leesbaar, niet omkeerbaar.</li>
            <li>JWT-sessies zijn kortlopend (7 dagen) en HttpOnly + SameSite=None+Secure.</li>
            <li>Brute-force bescherming: na 5 mislukte inlogpogingen 15 minuten cooldown per IP+e-mail.</li>
            <li>Betaal­gegevens (creditcard, IBAN) worden niét bij ons opgeslagen — die staan bij Mollie (PCI-DSS Level 1).</li>
            <li>Rol-gebaseerde toegangscontrole en owner-scoped queries — user A kan nooit bij data van user B.</li>
          </ul>
        </Section>

        <Section title="7. Jouw rechten (art. 15-22 AVG)" testid="privacy-rights">
          <ul className="list-disc pl-6 space-y-1">
            <li><b>Recht op inzage</b> — download alle data via "Download JSON" op je account-pagina.</li>
            <li><b>Recht op rectificatie</b> — wijzig factuur- en accountgegevens direct via je account-pagina.</li>
            <li><b>Recht op vergetelheid</b> — knop "Verwijder mijn account" op je account-pagina (of mail ons).</li>
            <li><b>Recht op dataportabiliteit</b> — de JSON-export is machine-leesbaar.</li>
            <li><b>Recht op bezwaar</b> — mail naar <a href={`mailto:${COMPANY.email}`} className="underline">{COMPANY.email}</a>.</li>
          </ul>
          <p className="text-xs text-muted-foreground">
            Vind je dat we je gegevens niet correct verwerken? Je kunt een klacht indienen bij de <a className="underline" href="https://autoriteitpersoonsgegevens.nl" target="_blank" rel="noopener noreferrer">Autoriteit Persoonsgegevens</a>.
          </p>
        </Section>

        <Section title="8. Cookies" testid="privacy-cookies">
          <p>
            We gebruiken alleen functionele cookies (session-cookie voor je login, Cloudflare-cookies voor DDoS-bescherming). Geen tracking- of advertentiecookies.
          </p>
        </Section>

        <Section title="9. Wijzigingen aan deze verklaring" testid="privacy-changes">
          <p>
            Als deze verklaring inhoudelijk wijzigt sturen we een e-mail naar alle actieve klanten. Deze versie is van <b>{lastUpdated}</b>.
          </p>
        </Section>

        <div className="mt-10">
          <Link to="/" className="text-sm text-muted-foreground hover:text-foreground underline">
            ← Terug naar dashboard
          </Link>
        </div>
      </div>
    </AppLayout>
  );
}
